import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { useAuth } from '../hooks/useAuth'

export default function Workspaces({ onSaved }) {
  const { config } = useAuth()
  const [workspaces, setWorkspaces] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [wsName, setWsName] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [error, setError] = useState(null)
  const [selectedWs, setSelectedWs] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [genPrompt, setGenPrompt] = useState('')
  const [genError, setGenError] = useState(null)
  const [aiDraft, setAiDraft] = useState(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDesc, setDraftDesc] = useState('')
  const [draftSteps, setDraftSteps] = useState([])
  const [saving, setSaving] = useState(false)
  const [listening, setListening] = useState(false)
  const [fileNames, setFileNames] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])

  useEffect(() => {
    if (selectedWs) {
      const fn = selectedWs.file_names || []
      setFileNames(fn)
      setSelectedFileIds(fn.map(f => f.id))
    }
  }, [selectedWs])

  useEffect(() => { fetchWorkspaces() }, [])

  async function fetchWorkspaces() {
    setLoading(true)
    const { data } = await supabase
      .from('workspaces')
      .select('*')
      .eq('client_id', config.id)
      .order('created_at', { ascending: false })
    setWorkspaces(data || [])
    setLoading(false)
  }

  async function deleteWorkspace(ws) {
    if (!window.confirm('Delete workspace ' + ws.name + '? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'workspaces', id: ws.id, label: 'workspace ' + ws.name, onUndone: () => fetchWorkspaces() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) }
    fetchWorkspaces()
  }

  function startListening(setter) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return alert('Voice not supported in this browser')
    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.onstart = () => setListening(true)
    recognition.onend = () => setListening(false)
    recognition.onresult = e => setter(e.results[0][0].transcript)
    recognition.onerror = () => setListening(false)
    recognition.start()
  }

  async function createWorkspace() {
    if (!wsName.trim()) return setError('Name is required')
    setCreating(true); setError(null)
    try {
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', name: wsName.trim(), vessel_name: config?.vessel_name })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      await supabase.from('workspaces').insert({
        client_id: config.id,
        name: wsName.trim(),
        openai_assistant_id: data.assistant_id,
        openai_vector_store_id: data.vector_store_id,
        file_ids: []
      })
      setWsName(''); setShowForm(false)
      fetchWorkspaces()
    } catch (err) {
      setError(err.message)
    }
    setCreating(false)
  }

  async function handleDrop(e, workspace) {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (!file) return
    await uploadFile({ target: { files: [file] } }, workspace)
  }

  async function uploadFile(e, workspace) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true); setUploadError(null)
    try {
      const buffer = await file.arrayBuffer()
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)))
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upload',
          file_data: base64,
          file_name: file.name,
          vector_store_id: workspace.openai_vector_store_id
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      const newFileIds = [...(workspace.file_ids || []), data.file_id]
      const newFileNames = [...(workspace.file_names || []), { id: data.file_id, name: file.name }]
      await supabase.from('workspaces').update({ file_ids: newFileIds, file_names: newFileNames }).eq('id', workspace.id)
      setFileNames(newFileNames)
      fetchWorkspaces()
      setSelectedWs({ ...workspace, file_ids: newFileIds, file_names: newFileNames })
    } catch (err) {
      setUploadError(err.message)
    }
    setUploading(false)
  }

  async function removeFile(fileId, workspace) {
    try {
      const newFileIds = (workspace.file_ids || []).filter(id => id !== fileId)
      const newFileNames = fileNames.filter(f => f.id !== fileId)
      await supabase.from('workspaces').update({ file_ids: newFileIds, file_names: newFileNames }).eq('id', workspace.id)
      setFileNames(newFileNames)
      setSelectedWs({ ...workspace, file_ids: newFileIds, file_names: newFileNames })
      fetchWorkspaces()
    } catch (err) {
      setUploadError(err.message)
    }
  }

  async function generateFromWorkspace() {
    if (!genPrompt.trim()) return setGenError('Describe what checklist you need')
    if (fileNames.length > 0 && selectedFileIds.length === 0) return setGenError('Select at least one document to generate from')
    setGenerating(true); setGenError(null)
    try {
      const selNames = fileNames.filter(f => selectedFileIds.includes(f.id)).map(f => f.name)
      let promptToSend = genPrompt
      if (fileNames.length > 1 && selNames.length > 0 && selNames.length < fileNames.length) {
        promptToSend = 'Use ONLY the following document(s) and ignore all other documents in the library: ' + selNames.join('; ') + '.\n\n' + genPrompt
      }
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate',
          assistant_id: selectedWs.openai_assistant_id,
          vector_store_id: selectedWs.openai_vector_store_id,
          prompt: promptToSend,
          vessel_name: config?.vessel_name
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setAiDraft(data)
      setDraftTitle(data.title)
      setDraftDesc(data.description || '')
      setDraftSteps((data.steps || []).map(st => typeof st === 'string' ? { text: st, type: 'check' } : { text: st.text || st.title || '', type: st.type || 'check' }))
    } catch (err) {
      setGenError(err.message)
    }
    setGenerating(false)
  }

  async function saveDraft() {
    if (!draftTitle.trim()) return setGenError('Title is required')
    const validSteps = draftSteps
      .map(st => typeof st === 'string' ? { text: st, type: 'check' } : { text: (st.text || st.title || ''), type: st.type || 'check' })
      .filter(st => st.text.trim())
    setSaving(true)
    try {
      const { data: cl, error: clErr } = await supabase
        .from('checklists')
        .insert({ client_id: config.id, workspace_id: selectedWs.id, title: draftTitle.trim(), description: draftDesc.trim(), source: 'ai', created_by: config?.vessel_name || 'crew' })
        .select().single()
      if (clErr) throw new Error(clErr.message)
      await supabase.from('checklist_items').insert(
        validSteps.map((st, i) => ({ checklist_id: cl.id, step_order: i + 1, title: st.text.trim(), required: st.type === 'check', is_header: st.type === 'header', is_note: st.type === 'note', is_attendance: st.type === 'attendance' }))
      )
      setAiDraft(null); setGenPrompt(''); setSelectedWs(null)
      if (onSaved) onSaved()
    } catch (err) {
      setGenError(err.message)
    }
    setSaving(false)
  }

  const s = styles

  if (aiDraft) return (
    <div style={s.wrap}>
      <button style={s.backBtn} onClick={() => setAiDraft(null)}>Back</button>
      <div style={s.form}>
        <h2 style={s.formTitle}>Review and edit before saving</h2>
        {genError && <p style={s.error}>{genError}</p>}
        <label style={s.label}>Title</label>
        <input style={s.input} value={draftTitle} onChange={e => setDraftTitle(e.target.value)} />
        <label style={s.label}>Description</label>
        <input style={s.input} value={draftDesc} onChange={e => setDraftDesc(e.target.value)} />
        <label style={s.label}>Steps</label>
        {draftSteps.map((step, i) => (
          <div key={i} style={{...s.stepRow, background: step.type==='header' ? 'rgba(184,150,62,.08)' : step.type==='note' ? 'rgba(106,170,212,.08)' : step.type==='attendance' ? 'rgba(93,186,142,.08)' : 'transparent', borderRadius:6}}>
            {step.type==='check' && <span style={s.stepNum}>{draftSteps.slice(0,i).filter(x=>x.type==='check').length + 1}</span>}
            <input style={{...s.input, flex:1, margin:0, fontWeight: step.type==='header'?700:400, color: step.type==='header'?'var(--gold2)':step.type==='note'?'#2a6a96':'var(--text)'}} value={step.text} onChange={e => { const ns=[...draftSteps]; ns[i]={...ns[i], text:e.target.value}; setDraftSteps(ns) }} />
            <button title="Header" style={{...s.removeBtn, color: step.type==='header'?'var(--gold2)':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='header'?'check':'header'}; setDraftSteps(ns) }}>H</button>
            <button title="Note" style={{...s.removeBtn, color: step.type==='note'?'#2a6a96':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='note'?'check':'note'}; setDraftSteps(ns) }}>N</button>
            <button title="Attendance" style={{...s.removeBtn, color: step.type==='attendance'?'#137050':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='attendance'?'check':'attendance'}; setDraftSteps(ns) }}>A</button>
            <button style={s.removeBtn} onClick={() => setDraftSteps(draftSteps.filter((_,idx)=>idx!==i))}>x</button>
          </div>
        ))}
        <button style={s.addStepBtn} onClick={() => setDraftSteps([...draftSteps,{ text:'', type:'check' }])}>+ Add step</button>
        <div style={s.formActions}>
          <button style={s.cancelBtn} onClick={() => setAiDraft(null)}>Back</button>
          <button style={s.saveBtn} onClick={saveDraft} disabled={saving}>{saving ? 'Saving...' : 'Save checklist'}</button>
        </div>
      </div>
    </div>
  )

  if (selectedWs) return (
    <div style={s.wrap}>
      <button style={s.backBtn} onClick={() => { setSelectedWs(null); setGenPrompt(''); setGenError(null) }}>Back</button>
      <h2 style={s.runnerTitle}>{selectedWs.name}</h2>
      <div style={s.card}>
        <p style={s.label}>Documents</p>
        {uploadError && <p style={s.error}>{uploadError}</p>}
        {fileNames.length > 0 && (
          <div style={{marginBottom: 8}}>
            {fileNames.map(f => (
              <div key={f.id} style={{display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)'}}>
                <input type="checkbox" checked={selectedFileIds.includes(f.id)} onChange={() => setSelectedFileIds(prev => prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id])} title="Include in generation" />
                <span style={{fontSize:13, color:'var(--text)', flex:1}}>{f.name}</span>
                <button style={s.removeBtn} onClick={() => removeFile(f.id, selectedWs)}>x</button>
              </div>
            ))}
          </div>
        )}
        {(selectedWs.file_ids || []).length > 0 && fileNames.length === 0 && (
          <p style={s.muted}>{(selectedWs.file_ids||[]).length} file{(selectedWs.file_ids||[]).length !== 1 ? 's' : ''} uploaded (names not tracked for existing files)</p>
        )}
        <input type="file" accept=".pdf,.txt,.doc,.docx" onChange={e => uploadFile(e, selectedWs)} style={{display:'none'}} id="ws-upload" />
        <label
          htmlFor="ws-upload"
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => handleDrop(e, selectedWs)}
          style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, marginTop:8, padding:'20px 16px', borderRadius:10, border: dragOver ? '2px dashed var(--gold2)' : '2px dashed var(--border)', background: dragOver ? 'rgba(184,150,62,.08)' : 'var(--chip)', cursor:'pointer', transition:'all .2s' }}
        >
          <span style={{fontSize:22}}>📄</span>
          <span style={{fontSize:13, color:'var(--text)', fontWeight:500}}>{uploading ? 'Uploading...' : 'Drop file here or click to upload'}</span>
          <span style={{fontSize:11, color:'var(--muted)'}}>PDF, TXT, DOC, DOCX</span>
        </label>
      </div>
      <div style={{...s.card, marginTop:12}}>
        <p style={s.label}>Generate checklist from documents</p>
        {fileNames.length > 1 && <p style={{...s.muted, fontSize:12, marginTop:-4, marginBottom:4}}>{selectedFileIds.length === fileNames.length ? 'Using all documents — untick any above to exclude.' : 'Using ' + selectedFileIds.length + ' of ' + fileNames.length + ' documents.'}</p>}
        {genError && <p style={s.error}>{genError}</p>}
        <div style={{position:'relative'}}>
          <textarea
            style={{...s.input, minHeight:80, resize:'vertical', marginTop:8, paddingRight:40}}
            placeholder="e.g. Create a pre-departure engine check from the uploaded manual"
            value={genPrompt}
            onChange={e => setGenPrompt(e.target.value)}
          />
          <button
            style={{position:'absolute', right:8, top:16, background: listening ? 'rgba(224,112,112,.2)' : 'none', border: listening ? '1px solid #b4313c' : '1px solid var(--border)', borderRadius:6, padding:'4px 8px', cursor:'pointer', color: listening ? '#b4313c' : 'var(--muted)', fontSize:16}}
            onClick={() => startListening(setGenPrompt)}
            title="Speak your prompt"
          >
            {listening ? 'o' : 'mic'}
          </button>
        </div>
        <div style={{marginTop:12}}>
          <button style={s.saveBtn} onClick={generateFromWorkspace} disabled={generating}>
            {generating ? 'Generating... (may take 20-30 seconds)' : 'Generate checklist'}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div style={s.wrap}>
      <button style={s.newBtn} onClick={() => setShowForm(true)}>+ New Workspace</button>
      {showForm && (
        <div style={{...s.card, marginBottom:16}}>
          <h3 style={{color:'var(--text)', marginBottom:12, fontWeight:600}}>Create workspace</h3>
          {error && <p style={s.error}>{error}</p>}
          <input style={s.input} placeholder="e.g. Engine Room Documents" value={wsName} onChange={e => setWsName(e.target.value)} />
          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setError(null) }}>Cancel</button>
            <button style={s.saveBtn} onClick={createWorkspace} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      )}
      {loading ? <p style={s.muted}>Loading...</p>
      : workspaces.length === 0 ? (
        <div style={{marginTop:'2rem'}}>
          <p style={s.muted}>No workspaces yet.</p>
          <p style={{...s.muted, marginTop:4}}>Create a workspace, upload your vessel documents, then generate accurate checklists from them.</p>
        </div>
      ) : (
        <div style={s.list}>
          {workspaces.map(ws => (
            <div key={ws.id} style={s.card}>
              <div style={s.cardTop}>
                <span style={s.cardTitle}>{ws.name}</span>
                <span style={{...s.badge, background:'rgba(106,170,212,.15)', color:'#2a6a96'}}>
                  {(ws.file_ids||[]).length} docs
                </span>
              </div>
              <div style={{display:'flex', gap:8, marginTop:10}}>
                <button style={s.saveBtn} onClick={() => setSelectedWs(ws)}>Open</button>
                <button style={s.histBtn} onClick={() => deleteWorkspace(ws)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '2rem', maxWidth: 720 },
  newBtn: { background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: '1.5rem' },
  backBtn: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', marginBottom: '1rem', padding: 0 },
  muted: { color: 'var(--muted)', fontSize: 14, margin: 0 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.25rem' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  cardTitle: { fontWeight: 600, fontSize: 15, color: 'var(--text)' },
  badge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20 },
  histBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 16px', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' },
  runnerTitle: { fontSize: '1.2rem', fontWeight: 600, color: 'var(--text)', marginBottom: 16 },
  form: { background: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', maxWidth: 600 },
  formTitle: { fontSize: '1.1rem', fontWeight: 600, marginBottom: '1.25rem', color: 'var(--text)' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, marginTop: 14, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { width: '100%', background: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  stepRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  stepNum: { width: 24, height: 24, borderRadius: '50%', background: 'var(--gold)', color: 'var(--navy)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  removeBtn: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, padding: '4px 8px' },
  addStepBtn: { background: 'none', border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--muted)', fontSize: 13, padding: '8px 16px', cursor: 'pointer', marginTop: 4, width: '100%' },
  formActions: { display: 'flex', gap: 12, marginTop: '1.5rem', justifyContent: 'flex-end' },
  cancelBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', padding: '10px 20px', cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  error: { color: '#b4313c', fontSize: 13, marginBottom: 12 },
}
